import React, { useEffect, useMemo, useState } from "react";
import { ExternalLink, File, FileImage, FileSpreadsheet, FileText, FolderOpen, Link2, Plus, Search, X } from "lucide-react";
import { toast } from "sonner";

import collaboratorApi from "../../../lib/collaboratorApi";
import { Card, EmptyState, PageHeader, Pill, TabSkeleton } from "./ui";

const EMPTY_DRAFT = { title: "", category: "Document", description: "", url: "" };
const normalize = (value) => String(value || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/** Only real web links are ever opened: anything else is shown as "no link". */
export function safeHttpUrl(value) {
  try {
    const url = new URL(String(value || "").trim());
    return url.protocol === "http:" || url.protocol === "https:" ? url.href : "";
  } catch {
    return "";
  }
}

/** Icon and label from the file extension in the link. */
export function documentKind(url) {
  const path = (() => { try { return new URL(url).pathname.toLowerCase(); } catch { return ""; } })();
  if (/\.pdf$/.test(path)) return { label: "PDF", Icon: FileText };
  if (/\.(png|jpe?g|webp|gif|heic)$/.test(path)) return { label: "Image", Icon: FileImage };
  if (/\.(xlsx?|csv|ods)$/.test(path)) return { label: "Tableur", Icon: FileSpreadsheet };
  if (/\.(docx?|odt|rtf|txt)$/.test(path)) return { label: "Document", Icon: FileText };
  return url ? { label: "Lien", Icon: Link2 } : { label: "Fiche", Icon: File };
}

const formatDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "short", year: "numeric" }).format(date);
};

export default function DocumentsTab() {
  const [documents, setDocuments] = useState(null);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    collaboratorApi
      .get("/collaborator-portal/documents")
      .then((response) => { if (!ignore) setDocuments(response.data || []); })
      .catch(() => { if (!ignore) setError("Impossible de charger les documents partagés."); });
    return () => { ignore = true; };
  }, []);

  const categories = useMemo(() => Array.from(new Set((documents || []).map((doc) => doc.category).filter(Boolean))).sort((a, b) => a.localeCompare(b, "fr")), [documents]);
  const visible = useMemo(() => {
    const needle = normalize(query.trim());
    return (documents || []).filter((doc) => (!category || doc.category === category) && (!needle || normalize(`${doc.title} ${doc.category} ${doc.description}`).includes(needle)));
  }, [documents, query, category]);

  async function handleCreateDocument(event) {
    event.preventDefault();
    if (!draft.title.trim() || saving) return;
    if (draft.url.trim() && !safeHttpUrl(draft.url)) {
      toast.error("Le lien doit commencer par http:// ou https://");
      return;
    }
    setSaving(true);
    try {
      const response = await collaboratorApi.post("/collaborator-portal/documents", {
        title: draft.title.trim(),
        category: draft.category,
        description: draft.description,
        url: draft.url.trim(),
      });
      setDocuments((prev) => [response.data, ...(prev || [])]);
      setDraft(EMPTY_DRAFT);
      setAdding(false);
      toast.success("Document partagé ajouté.");
    } catch (createError) {
      toast.error(createError.response?.data?.detail || "Impossible d’ajouter ce document.");
    } finally {
      setSaving(false);
    }
  }

  if (error) return <div role="alert" className="w-full rounded-2xl border border-red-100 bg-red-50 p-6 text-sm text-red-700">{error}</div>;
  if (!documents) return <TabSkeleton />;

  const field = "w-full rounded-xl border border-[#e2d9c8] bg-[#fbf8f2] px-3.5 py-2.5 text-sm text-[#211d19] outline-none placeholder:text-[#8b7f70] focus:border-[#5f7d68] focus:bg-white";

  return (
    <div className="w-full space-y-6 pb-8">
      <PageHeader
        eyebrow="Espace partagé"
        title="Documents"
        subtitle="Contrats, plans, devis et fiches partagés entre les mariés et l'équipe."
        actions={(
          <button type="button" onClick={() => setAdding((open) => !open)} aria-expanded={adding} className="flex items-center gap-2 rounded-xl bg-[#211d19] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-black">
            {adding ? <X className="h-4 w-4" aria-hidden="true" /> : <Plus className="h-4 w-4" aria-hidden="true" />}
            {adding ? "Fermer" : "Ajouter un document"}
          </button>
        )}
      />

      {adding ? (
        <Card>
          <form onSubmit={handleCreateDocument} className="grid gap-3 md:grid-cols-2" aria-label="Nouveau document">
            <label className="text-xs font-semibold text-[#4a4239]">Nom du document
              <input className={`${field} mt-1`} type="text" required placeholder="Ex. Devis traiteur" value={draft.title} onChange={(event) => setDraft((prev) => ({ ...prev, title: event.target.value }))} />
            </label>
            <label className="text-xs font-semibold text-[#4a4239]">Catégorie
              <input className={`${field} mt-1`} type="text" list="doc-categories" placeholder="Contrat, Plan, Devis…" value={draft.category} onChange={(event) => setDraft((prev) => ({ ...prev, category: event.target.value }))} />
              <datalist id="doc-categories">{categories.map((name) => <option key={name} value={name} />)}</datalist>
            </label>
            <label className="text-xs font-semibold text-[#4a4239] md:col-span-2">Description
              <input className={`${field} mt-1`} type="text" placeholder="Facultatif" value={draft.description} onChange={(event) => setDraft((prev) => ({ ...prev, description: event.target.value }))} />
            </label>
            <label className="text-xs font-semibold text-[#4a4239] md:col-span-2">Lien du document
              <input className={`${field} mt-1`} type="url" placeholder="https://…" value={draft.url} onChange={(event) => setDraft((prev) => ({ ...prev, url: event.target.value }))} />
            </label>
            <div className="flex justify-end gap-2 md:col-span-2">
              <button type="button" onClick={() => { setAdding(false); setDraft(EMPTY_DRAFT); }} className="rounded-xl border border-[#e2d9c8] px-4 py-2.5 text-sm font-semibold text-[#4a4239] hover:bg-[#f6f1e8]">Annuler</button>
              <button type="submit" disabled={saving || !draft.title.trim()} className="rounded-xl bg-[#5f7d68] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#4f6a58] disabled:opacity-50">{saving ? "Ajout…" : "Ajouter"}</button>
            </div>
          </form>
        </Card>
      ) : null}

      {documents.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Aucun document partagé" text="Ajoutez un premier document pour le partager avec l'équipe." />
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label className="relative min-w-[220px] flex-1">
              <span className="sr-only">Rechercher un document</span>
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8b7f70]" aria-hidden="true" />
              <input type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un document" className={`${field} pl-10`} />
            </label>
            {categories.length > 1 ? (
              <div className="-mx-1 flex max-w-full gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Catégories">
                {["", ...categories].map((name) => (
                  <button key={name || "all"} type="button" role="tab" aria-selected={category === name} onClick={() => setCategory(name)} className={`shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-semibold ${category === name ? "border-[#211d19] bg-[#211d19] text-white" : "border-[#e2d9c8] bg-white text-[#5b5247] hover:bg-[#f6f1e8]"}`}>{name || "Tous"}</button>
                ))}
              </div>
            ) : null}
          </div>

          {visible.length === 0 ? (
            <EmptyState icon={Search} title="Aucun document ne correspond" text="Modifiez la recherche ou la catégorie." />
          ) : (
            <ul className="grid gap-3 md:grid-cols-2">
              {visible.map((doc) => {
                const href = safeHttpUrl(doc.url);
                const { label, Icon } = documentKind(href);
                const body = (
                  <>
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#eef3ec] text-[#3f5a48]"><Icon className="h-6 w-6" aria-hidden="true" /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block break-words font-semibold text-[#211d19]">{doc.title}</span>
                      {doc.description ? <span className="mt-0.5 block text-sm text-[#4a4239]">{doc.description}</span> : null}
                      <span className="mt-2 flex flex-wrap items-center gap-2">
                        {doc.category ? <Pill tone="info">{doc.category}</Pill> : null}
                        <Pill tone="neutral">{label}</Pill>
                        {formatDate(doc.created_at) ? <span className="text-xs text-[#6a6055]">{formatDate(doc.created_at)}</span> : null}
                      </span>
                    </span>
                    {href ? <ExternalLink className="h-4 w-4 shrink-0 text-[#8b7f70] transition-colors group-hover:text-[#211d19]" aria-hidden="true" /> : null}
                  </>
                );
                return (
                  <li key={doc.id}>
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 rounded-2xl border border-[#e9e1d4] bg-white p-4 shadow-sm transition-all hover:-translate-y-0.5 hover:border-[#bfd0bb] hover:shadow-md">{body}</a>
                    ) : (
                      <div className="flex items-start gap-4 rounded-2xl border border-[#e9e1d4] bg-white p-4 shadow-sm">{body}</div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </>
      )}
    </div>
  );
}
